import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AdminUser, AuthStatus } from './types';
import type { TokenPairResponse } from '@/api/schemas/auth';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AdminUser | null;
  status: AuthStatus;
  setSession: (tokens: TokenPairResponse, user: AdminUser) => void;
  setTokens: (tokens: TokenPairResponse) => void;
  setUser: (user: AdminUser) => void;
  setStatus: (status: AuthStatus) => void;
  clear: () => void;
}

// La sesión se persiste en sessionStorage: sobrevive a un reload/refresh de la
// pestaña (arregla el cierre de sesión al refrescar) pero se descarta al cerrar
// la pestaña/navegador — coherente con la postura de seguridad del panel
// (MFA + auto-logout por inactividad). El interceptor 401 refresca el access
// token expirado usando el refreshToken rehidratado.
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      status: 'unauthenticated',
      setSession: (tokens, user) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user,
          status: 'authenticated',
        }),
      setTokens: (tokens) =>
        set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }),
      setUser: (user) => set({ user }),
      setStatus: (status) => set({ status }),
      clear: () =>
        set({ accessToken: null, refreshToken: null, user: null, status: 'unauthenticated' }),
    }),
    {
      name: 'ko-admin-auth',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        status: s.status,
      }),
    },
  ),
);
